import * as React from 'react'

import { useUserProfile } from './hooks'
import { Button, Card, Col, Row, Space, Table, Tag, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useUnit } from 'effector-react'
import { PermissionRef, User } from './types'
import { path } from '../../../../libs'
import {
  $userEffectivePermissions,
  $userGroups,
  $userRoles,
  getUserEffectivePermissions,
} from './model'
import { useNavigate } from 'react-router-dom'

const { Text, Title } = Typography

type Props = {
  user: User
}

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div style={{ marginTop: 8 }}>
    <Text type="secondary">{label}:</Text> {value || '—'}
  </div>
)

export const UserProfile = ({ user }: Props) => {
  return (
    <Card>
      <Row gutter={16}>
        <Col sm={16}>
          <Space align="center" size="large" style={{ marginBottom: 12 }}>
            {user.avatarUrl?.length > 0 && (
              <img
                src={user.avatarUrl}
                alt={user.fullName}
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            )}
            <Title level={3} style={{ margin: 0 }}>
              {user.fullName}
            </Title>
          </Space>
          <Field label="First name" value={user.firstName} />
          <Field label="Middle name" value={user.middleName} />
          <Field label="Last name" value={user.lastName} />
          <Field label="e-mail" value={user.email} />
          <Field label="Position" value={user.position} />
        </Col>
      </Row>
    </Card>
  )
}

const UserAccessSections: React.FC = () => {
  const navigate = useNavigate()
  const groups = useUnit($userGroups)
  const roles = useUnit($userRoles)
  const permissions = useUnit($userEffectivePermissions)
  const permissionsLoading = useUnit(getUserEffectivePermissions.pending)

  const permissionColumns: ColumnsType<PermissionRef> = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    { title: 'Description', dataIndex: 'description' },
  ]

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card title="Groups">
        {groups.length === 0 ? (
          <Text type="secondary">No groups assigned</Text>
        ) : (
          <Space wrap>
            {groups.map((g) => (
              <Tag
                key={g.id}
                color="geekblue"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(path.groups.edit(g.id))}
              >
                {g.name}
              </Tag>
            ))}
          </Space>
        )}
      </Card>

      <Card
        title="Inherited roles"
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Roles derived from assigned groups (hierarchy flattened)
          </Text>
        }
      >
        {roles.length === 0 ? (
          <Text type="secondary">No roles inherited</Text>
        ) : (
          <Space wrap>
            {roles.map((r) => (
              <Tag
                key={r}
                color="blue"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(path.roles.details(r))}
              >
                {r}
              </Tag>
            ))}
          </Space>
        )}
      </Card>

      <Card
        title="Effective permissions"
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Final permission set after combining role grants, extras and
            revocations
          </Text>
        }
      >
        <Table
          dataSource={permissions}
          columns={permissionColumns}
          loading={permissionsLoading}
          bordered
          rowKey="id"
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>
    </Space>
  )
}

export const UserDetail = () => {
  const userProfile = useUserProfile()
  const navigate = useNavigate()

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row justify="end">
        <Button onClick={() => navigate(path.users.edit(userProfile.id))}>
          Edit
        </Button>
      </Row>
      <UserProfile user={userProfile} />
      <UserAccessSections />
    </Space>
  )
}
